// Table primitives shared by the My Trades tables (index.tsx and
// UnifiedTradesTable). Extracted from index.tsx so the unified table can
// import them without a module cycle. MoneyCell/PnlCell are the single home
// for the money-column rules both cross-venue tables must agree on: masked
// under the #124 P&L privacy preference, and '—' for unavailable values —
// never a fabricated $0.00.

import type { ReactNode } from 'react';
import { maskPnl, maskPnlColor } from '../../lib/pnlMask';
import { formatMoney } from '../../lib/infra/formatters';
import { fmtCents, fmtQty } from '../../components/cards/cardFormat';
import type { TradeCardVM } from '../../components/cards/TradeCard';
import { formatUsSignedUsd, isUsZero } from '../../lib/polymarketUs/format';
import { US_PROVISIONAL_TITLE } from '../../lib/fills/usPrice';

export function Th({
  children,
  align = 'left',
  title,
  sub,
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  title?: string;
  /** Optional second-row qualifier (e.g. "estimated", "actual"). Renders
   *  smaller, normal-case, dimmed beneath the main header. Lets us
   *  reclaim horizontal space when the qualifier is repeated across
   *  several columns. */
  sub?: string;
}) {
  const alignCls =
    align === 'right'
      ? 'text-right'
      : align === 'center'
        ? 'text-center'
        : 'text-left';
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 align-top text-[11px] font-semibold uppercase tracking-wider ${alignCls}`}
      title={title}
    >
      <div>{children}</div>
      {sub && (
        <div className="-mt-0.5 text-[9px] font-normal normal-case tracking-normal opacity-60">
          {sub}
        </div>
      )}
    </th>
  );
}

export function formatDateOnly(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Right-aligned dollar cell. `text` (#1227) is a Polymarket US row's exact
 *  amount, shown as given; `title` names why a dash is a dash. */
export function MoneyCell({
  dollars,
  hidePnl,
  text,
  title,
}: {
  dollars: number | null;
  hidePnl: boolean;
  text?: string | null;
  title?: string | null;
}) {
  const shown = text !== undefined ? text ?? '—' : dollars == null ? '—' : formatMoney(dollars);
  return (
    <td
      className="text-muted-foreground px-3 py-1.5 text-right font-mono"
      title={shown === '—' ? title ?? undefined : undefined}
    >
      {maskPnl(hidePnl, shown)}
    </td>
  );
}

/** #1227 — a trade row's money cells read the US exact text when the row has
 *  it (`vm.us`), else the venue's numbers. One spread per cell keeps the three
 *  trade tables and the card on one rule. */
export const tradeMoney = (vm: TradeCardVM, field: 'fees' | 'totalIn' | 'cashOut') => {
  const dollars = field === 'fees' ? vm.feesDollars : field === 'totalIn' ? vm.totalInDollars : vm.cashOutDollars;
  if (!vm.us) return { dollars };
  const text = field === 'fees' ? vm.us.feesText : field === 'totalIn' ? vm.us.totalInText : vm.us.cashOutText;
  // An OPEN row's cash out is a dash by nature, not for want of evidence.
  return { dollars, text, title: field === 'cashOut' && vm.action === 'OPEN' ? null : vm.us.dashTitle };
};

export const tradePnl = (vm: TradeCardVM) =>
  vm.us
    ? { dollars: vm.netPnlDollars, text: vm.us.netPnlText, title: vm.action === 'OPEN' ? null : vm.us.dashTitle }
    : { dollars: vm.netPnlDollars };

/** Quantity as the row gives it: a US row's exact text, else the whole-number count. */
export const tradeQty = (vm: TradeCardVM) => vm.us?.qtyText ?? fmtQty(vm.qty);

/** A per-contract price in ¢; a provisional US price (a NO fill's 1 − YES) carries `*`. */
export function TradePrice({ cents, provisional = false }: { cents: number | null; provisional?: boolean }) {
  return (
    <>
      {fmtCents(cents)}
      {provisional && (
        <span className="text-muted-foreground/60" title={US_PROVISIONAL_TITLE}>*</span>
      )}
    </>
  );
}

/** Net P&L cell — signed, green/red/neutral by sign, muted when unknown.
 *  `text` (#1227) is a US row's exact signed amount; its sign drives the tone. */
export function PnlCell({
  dollars,
  hidePnl,
  text,
  title,
}: {
  dollars: number | null;
  hidePnl: boolean;
  text?: string | null;
  title?: string | null;
}) {
  if (text !== undefined) {
    const tone = text === null ? 'text-muted-foreground' : isUsZero(text) ? 'text-foreground'
      : text.startsWith('-') ? 'text-red-400' : 'text-green-400';
    const shown = text === null ? '—' : formatUsSignedUsd(text);
    return (
      <td
        className={`px-3 py-1.5 text-right font-mono font-medium ${maskPnlColor(hidePnl, tone)}`}
        title={text === null ? title ?? undefined : undefined}
      >
        {maskPnl(hidePnl, shown)}
      </td>
    );
  }
  return (
    <td
      className={`px-3 py-1.5 text-right font-mono font-medium ${maskPnlColor(
        hidePnl,
        dollars == null
          ? 'text-muted-foreground'
          : dollars > 0
            ? 'text-green-400'
            : dollars < 0
              ? 'text-red-400'
              : 'text-foreground',
      )}`}
    >
      {maskPnl(hidePnl, dollars == null ? '—' : formatMoney(dollars, { showSign: true }))}
    </td>
  );
}
